import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth-store';
import { usePermissionsStore, type PermissionModule } from '../../../stores/permissions-store';

interface PermissionRouteProps {
  /** The permission module required to view this route */
  module: PermissionModule;
  /** The action required (defaults to 'view') */
  action?: string;
  /** The page component to render */
  children: React.ReactNode;
}

/**
 * Route-level permission guard. Redirects to /dashboard with a 403
 * if the user lacks the required permission.
 * Super admin (role === 'super_admin') bypasses all permission checks (WAYP-288).
 *
 * Usage in App.tsx:
 *   <Route path="/invoices" element={
 *     <PermissionRoute module="invoices">
 *       <InvoicesPage />
 *     </PermissionRoute>
 *   } />
 */
export function PermissionRoute({
  module,
  action = 'view',
  children,
}: PermissionRouteProps) {
  const user = useAuthStore((s) => s.user);
  const can = usePermissionsStore((s) => s.can);
  const loaded = usePermissionsStore((s) => s.loaded);

  // Super admin bypasses all permission checks
  if (user?.role === 'super_admin') {
    return <>{children}</>;
  }

  // Don't block while permissions are still loading
  if (!loaded) {
    return <>{children}</>;
  }

  if (!can(module, action)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
